// src/encounter/imaging-add-dialog.tsx
// Imaging (radiology) order dialog for the A/P canvas — composed on OrderDialogShell (PROD-1801).
//
// PARITY REBUILD, 2026-07-30. Ported from the LIVE Flutter dialog (dev-provider.scrips.com, captured
// 2026-07-30 — "Ht muscle image spect sing", 78451) and Figma Encounter-2 `1736:159053`
// (`appointments@patient@ap@edit-radiology`). The shell carries everything labs, radiology and
// procedure share; radiology adds nothing of its own, which is why this file is now thin.
//
// What the rebuild adds, all of it design that existed and was never built:
//   • the `DIAGNOSIS (n) *` multi-select — Samer asked for it on 2026-07-29, and the frame has
//     specified it since Jan '23. It returns the FULL set, so an order can justify N diagnoses;
//   • the read-only header (field label + study + code) instead of a bare selected-chip;
//   • the note's `0/300` counter, by passing the DS TextArea's own `maxLength`.
//
// The study stays re-searchable when the dialog is opened cold ("+ Imaging"), because the user has
// nothing yet. Seeded from a drag, the header is read-only — matching both the frame's filled state
// and Flutter's seeded `radiology_add_widget`.
//
// orderId: derived from the picked code + idSuffix (NOT Math.random/Date.now) — stable for a given
// pick, and the suffix lets the host disambiguate the same study attached more than once.
import { useState } from 'react';
import { CodeSearchField, type CodeSearchResult } from '@scripsteam/scrips-signal-ds';
import { useRadiologySearch } from './services/use-radiology-search';
import { toImagingOrder, type ImagingOrderResult } from './lib/to-imaging-order';
import { OrderDialogShell } from './order-dialog-shell';
import type { DiagnosisOption } from './diagnosis-multi-select';

/** The confirm payload, widened with the diagnoses the clinician linked (PROD-1801). */
export interface ImagingConfirm extends ImagingOrderResult {
  /** Every linked diagnosis, in canvas order — dxIds[0] is the nesting parent. */
  dxIds: string[];
}

export interface ImagingAddDialogProps {
  /** Practitioner id — scopes the provider radiology catalog (null → master-catalog fallback). */
  providerId: string | null | undefined;
  /** The encounter's diagnoses, for the DIAGNOSIS block. */
  diagnoses: DiagnosisOption[];
  /**
   * Pre-ticked diagnoses. The canvas seeds this with the Dx the picker was opened from, so the
   * common single-Dx flow needs no extra clicks; the clinician can then tick more.
   */
  initialDxIds?: readonly string[];
  /** Stable suffix folded into the generated orderId (the host increments it per attachment). */
  idSuffix?: string | number;
  /**
   * Pre-selected study (seeded from a dragged panel item — PROD-1419). When present the study is
   * already picked, so a dragged X-ray needs NO manual re-search and the header is read-only.
   */
  initialItem?: CodeSearchResult;
  /** Editing an existing order (PROD-1433): pre-fill the note + switch title/confirm copy to "Edit". */
  initialNote?: string;
  editing?: boolean;
  onConfirm: (order: ImagingConfirm) => void;
  /**
   * Destructive DELETE, bottom-right — the red trash glyph in the Figma frames. Editing only (the
   * host passes its existing order-removal callback); omitted, nothing renders.
   */
  onDelete?: () => void;
  onCancel: () => void;
  /**
   * AUTHORING an order set (PROD-2714): the same form with no encounter. The DIAGNOSIS block and the
   * note are not shown (see OrderDialogShell `authoring`).
   */
  authoring?: boolean;
}

export function ImagingAddDialog({
  diagnoses,
  initialDxIds,
  idSuffix = '0',
  initialItem,
  initialNote,
  editing,
  onConfirm,
  onCancel,
  onDelete,
  authoring = false,
}: ImagingAddDialogProps) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<CodeSearchResult | undefined>(initialItem);
  const [note, setNote] = useState(initialNote ?? '');
  const [dxIds, setDxIds] = useState<string[]>(() => [...(initialDxIds ?? [])]);

  // Global/master catalog (providerId omitted) — matches the A/P panel source so a dragged imaging
  // item is findable even when provider favourites are empty (PROD-1421; Codex #145).
  const search = useRadiologySearch(undefined, query);

  function handleConfirm() {
    if (!selected) return;
    const orderId = `imaging-${selected.code ?? selected.id}-${idSuffix}`;
    onConfirm({ ...toImagingOrder(selected, orderId, note), dxIds });
  }

  return (
    <OrderDialogShell
      authoring={authoring}
      title={editing ? 'Edit imaging' : 'Add imaging'}
      fieldLabel="Radiology"
      itemName={selected?.display ?? 'No study selected'}
      itemCode={selected?.code}
      diagnoses={diagnoses}
      selectedDxIds={dxIds}
      onDxChange={setDxIds}
      note={note}
      onNoteChange={setNote}
      // ONE SAVE LABEL across every A/P dialog (Andrew, 1 Aug). Flutter's shared add widget defaults to
      // S.of(context).save — "Save" — for all of them (base_ap_item_add_widget.dart:288-290). We had
      // grown THREE: "Save", "Confirm", and "Add to plan". The shell already defaults to Save, so the
      // per-dialog override just goes.
      // The shell already blocks a save with zero diagnoses; this adds "a study must be chosen".
      canSave={!!selected}
      onSave={handleConfirm}
      onCancel={onCancel}
      onDelete={onDelete}
      // Cold open ("+ Imaging") keeps the search, because the user has nothing yet. Seeded from a drag
      // the study is fixed, matching the frame's filled state and Flutter's seeded widget.
      searchSlot={
        initialItem ? undefined : (
          <CodeSearchField
            value={query}
            onChange={setQuery}
            results={search.data ?? []}
            loading={search.isFetching}
            onResultClick={setSelected}
            placeholder="Search radiology studies…"
          />
        )
      }
    />
  );
}
